/**
 * Chat surface. Hosts the message list, input bar, and SSE streaming
 * lifecycle. Two entry modes:
 *
 *   - Fresh session (initialMode set, kickoffMessage optional): on mount,
 *     auto-fires the kickoff message so the user lands in dialogue.
 *   - Resume (conversationId set): on mount, fetches the conversation
 *     and renders existing messages; new messages append.
 *
 * Mirrors iOS ChatView; SwiftUI patterns translated to React (useState
 * + useEffect for lifecycle; useRef + scrollIntoView for auto-scroll).
 */

import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import {
  CapReachedError,
  formatActionChoice,
  getConversation,
  streamChat,
  type ChatStreamRequest,
} from "@/shared/api";
import { dailyChallengeRoute } from "@/shared/chatRoute";
import {
  DIFFICULTY_LABEL,
  getDailyChallenge,
  nextIncompleteDifficulty,
  type DailyChallenge,
} from "@/features/daily/lib/dailyChallenge";
import { useUpgradeModal } from "@/features/billing/components/UpgradeModal";
import type { DrillDifficulty, Message, SpotAction, SpotPayload } from "@/shared/models";
import { invalidateAfterTurn } from "@/shared/queryInvalidation";
import { parseSegments } from "@/features/chat/lib/messageSegments";
import { ChevronRight, ICON_STROKE } from "@/shared/components/icons";
import { MessageBubble } from "@/features/chat/components/MessageBubble";
import { SheetModal } from "@/features/chat/components/SheetModal";

interface ChatViewProps {
  /** Mode for new conversations; ignored when resuming. */
  initialMode?: string;
  /** When set, fires as the first user message on mount. */
  kickoffMessage?: string;
  /** When set, resumes an existing conversation. */
  resumeConversationId?: string;
  /** Heading shown above the chat list. */
  title?: string;
  /** Structured drill metadata — what skill this drill is exercising
   * and how hard the spots should be. Threaded into every ChatRequest
   * so backend can compose the addendum + do library lookup. Optional
   * for free-form chats (Just Ask, Concept Deep Dive). */
  drillTarget?: string;
  drillDifficulty?: "foundational" | "default" | "edge_case";
  /** Today's Drill: the user's local date (YYYY-MM-DD). When set, every
   * turn is part of the shared daily challenge (cap-exempt, pinned spot,
   * streak on completion). */
  dailyChallengeDate?: string;
  /** Session-tracker coaching: binds a debrief chat to a logged session so
   * the backend injects that session's [SESSION FACTS] + routes signals to
   * the session_insights stream. */
  sessionId?: string;
  /** Scope the warmup/consult coaching to one tracker segment — the backend
   * computes [SESSION FACTS] over just this stake/location. */
  sessionStake?: string;
  sessionLocation?: string;
  /** Hand Analyzer: binds the chat to a saved built hand so the backend
   * injects its [HAND REPLAY] block and the coach grades the line. */
  handId?: string;
}

export function ChatView({
  initialMode,
  kickoffMessage,
  resumeConversationId,
  title,
  drillTarget,
  drillDifficulty,
  dailyChallengeDate,
  sessionId,
  sessionStake,
  sessionLocation,
  handId,
}: ChatViewProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversationId, setConversationId] = useState<string | undefined>(
    resumeConversationId,
  );
  // Resolved conversation mode. Fresh sessions get it from `initialMode`;
  // resumed sessions read it from the fetched conversation doc. Drives
  // whether the free-form input bar is available (see isConversationalMode).
  const [resolvedMode, setResolvedMode] = useState<string | undefined>(
    initialMode,
  );
  const [draft, setDraft] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(
    !!resumeConversationId,
  );
  const [openSheetId, setOpenSheetId] = useState<string | null>(null);
  const hasStartedRef = useRef(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const upgradeModal = useUpgradeModal();
  const queryClient = useQueryClient();
  // Defense-in-depth against the race where a SpotCard or ChoicesBlock
  // button fires during an in-flight stream. The UI gating (locked prop)
  // is the primary defense; this ref is the belt-and-suspenders catch
  // in case a button click slips through before React state updates.
  const isStreamingRef = useRef(false);

  // ---- Resume vs. kickoff (run once) ----
  useEffect(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;
    (async () => {
      if (resumeConversationId) {
        try {
          const detail = await getConversation(resumeConversationId);
          setMessages(detail.messages);
          setConversationId(detail.id);
          if (detail.mode) setResolvedMode(detail.mode);
        } catch (err) {
          setErrorMessage(
            err instanceof Error ? err.message : "Failed to load conversation",
          );
        } finally {
          setIsLoadingHistory(false);
        }
        return;
      }
      if (kickoffMessage) {
        await sendMessage(kickoffMessage);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Auto-scroll to latest message ----
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  // ---- Send + stream ----
  async function sendMessage(text: string) {
    const trimmed = text.trim();
    if (!trimmed) return;
    // Defense-in-depth: if a stream is already in flight, drop the new
    // send. React state for isStreaming may not be settled when buttons
    // are clicked rapidly; the ref is set synchronously below. Without
    // this, a fast "Next spot" click during an in-flight bet response
    // would fire a second concurrent /chat request on the same
    // conversation_id, producing duplicate verdicts and polluted signals.
    if (isStreamingRef.current) return;
    isStreamingRef.current = true;

    setErrorMessage(null);
    const userMsg: Message = {
      id: `u-${Date.now()}`,
      role: "user",
      content: trimmed,
      timestamp: new Date().toISOString(),
    };
    const assistantId = `a-${Date.now()}`;
    const assistantMsg: Message = {
      id: assistantId,
      role: "assistant",
      content: "",
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMsg, assistantMsg]);
    setDraft("");
    setIsStreaming(true);

    try {
      const req: ChatStreamRequest = {
        message: trimmed,
        conversationId,
        mode: initialMode,
        drillTarget,
        drillDifficulty,
        dailyChallengeDate,
        sessionId,
        sessionStake,
        sessionLocation,
        handId,
      };
      for await (const event of streamChat(req)) {
        if (event.kind === "meta") {
          setConversationId(event.conversationId);
        } else if (event.kind === "token") {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? { ...m, content: m.content + event.chunk }
                : m,
            ),
          );
        } else if (event.kind === "error") {
          setErrorMessage(event.message);
        }
      }
    } catch (err) {
      if (err instanceof CapReachedError) {
        // Daily/monthly cap hit — pop the upgrade modal and remove the
        // empty assistant placeholder we appended (no reply will ever
        // land). UpgradeModal renders different copy based on
        // err.window (daily vs monthly).
        upgradeModal.open({
          window: err.window,
          gateType: err.gateType,
          subscriptionStatus: err.subscriptionStatus,
          planLevel: err.planLevel,
          used: err.used,
          limit: err.limit,
        });
        setMessages((prev) => prev.filter((m) => m.id !== assistantId));
      } else {
        setErrorMessage(err instanceof Error ? err.message : "Stream failed");
      }
    } finally {
      setIsStreaming(false);
      isStreamingRef.current = false;
      // Refresh billing-status (usage chip + Settings) after every turn; daily
      // turns also refresh the daily-challenge state. ChatView serves non-drill
      // modes too, so it does NOT invalidate the profile cluster (DrillView,
      // which is always a drill, does). See queryInvalidation.
      invalidateAfterTurn(queryClient, { isDaily: !!dailyChallengeDate });
    }
  }

  // ---- Locking: hide input bar when a spot or choices block is unresolved ----
  const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");
  const lastAssistantSegments = lastAssistant
    ? parseSegments(lastAssistant.content)
    : [];
  const waitingForSpotAction = lastAssistantSegments.some(
    (s) => s.kind === "spot" || s.kind === "choices",
  );

  // ---- Today's Drill footer ----
  // In daily mode we suppress the coach's "Next spot" choices (they'd
  // re-serve the same pinned spot) and render our own ladder navigation.
  // The footer shows once a verdict has landed: the last assistant
  // message has content but NO spot segment (a spot turn still awaits the
  // user's action). Reads the daily-challenge state (refetched after each
  // daily verdict) to know the next rung.
  const isDaily = !!dailyChallengeDate;
  const { data: dailyChallenge } = useQuery({
    queryKey: ["daily-challenge"],
    queryFn: getDailyChallenge,
    staleTime: 60_000,
    enabled: isDaily,
  });
  const lastIsVerdict =
    lastAssistantSegments.length > 0 &&
    !lastAssistantSegments.some((s) => s.kind === "spot");
  const showDailyFooter = isDaily && !isStreaming && lastIsVerdict;

  function spotsLockedAtIndex(idx: number): boolean {
    return messages.slice(idx + 1).some((m) => m.role === "user");
  }

  // The free-form "Ask…" input belongs in conversational modes: Just Ask
  // and the three session-tracker coaching modes (warmup / debrief /
  // consult), which are dialogues the user types back into. Spot drills use
  // SpotCard actions + choices, and Concept Deep Dive ends each turn with a
  // "where next?" choices block — in those, the input just flashes in/out
  // between turns. While a resumed conversation's mode is still loading
  // (resolvedMode undefined) the message area shows the history loader, so
  // suppressing the input briefly is invisible.
  const SESSION_COACHING_MODES = new Set([
    "session_warmup",
    "session_debrief",
    "session_consult",
  ]);
  const isSessionCoaching =
    resolvedMode !== undefined && SESSION_COACHING_MODES.has(resolvedMode);
  // Hand Analyzer is conversational like session coaching: the coach grades the
  // hand, then the user can ask follow-ups, so the input must stay visible even
  // alongside a `choices` block (no constructed spot to action).
  const isHandAnalysis = resolvedMode === "hand_analysis";
  const isFreeFormMode =
    resolvedMode === "just_ask" || isSessionCoaching || isHandAnalysis;

  return (
    <div className="flex flex-col h-[calc(100dvh-3rem-5rem-env(safe-area-inset-top)-env(safe-area-inset-bottom))] md:h-screen">
      {title && (
        <div className="px-4 md:px-8 py-3 border-b border-border bg-surface/50 backdrop-blur sticky top-0 md:top-0 z-[5]">
          <h1 className="text-lg font-semibold text-text">{title}</h1>
        </div>
      )}

      <div className="flex-1 overflow-y-auto scroll-momentum">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-6 space-y-3">
          {isLoadingHistory && (
            <div className="text-center text-text-muted py-12">
              Loading conversation…
            </div>
          )}
          {messages.map((message, idx) => (
            <MessageBubble
              key={message.id}
              message={message}
              // Lock the message's interactive elements (SpotCard action
              // buttons, ChoicesBlock options) either when a later user
              // message exists (historical resolved spots stay frozen)
              // OR while a stream is in-flight (prevents rapid double-
              // clicks from firing concurrent /chat requests).
              locked={spotsLockedAtIndex(idx) || isStreaming}
              onSpotAction={(action, spot) => sendActionChoice(action, spot)}
              onChoice={(option) => sendMessage(option)}
              onSheetLink={(id) => setOpenSheetId(id)}
              conversationId={conversationId}
              hideChoices={isDaily}
            />
          ))}
          {showDailyFooter && dailyChallenge && (
            <DailyDrillChoices
              challenge={dailyChallenge}
              onWalkThrough={() =>
                sendMessage("Walk me through the reasoning on that hand.")
              }
            />
          )}
          {messages.length === 0 && !isLoadingHistory && (
            <div className="text-center text-text-muted py-20">
              Start by typing a question or running a drill.
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {errorMessage && (
        <div className="px-4 md:px-8 py-2 text-xs text-danger text-center">
          {errorMessage}
        </div>
      )}

      {/* Session-coaching modes keep the input visible even when the coach
          offers a `choices` block (e.g. "Drill that leak") — the buttons are
          shortcuts, but typing a reply is the primary interaction. They never
          emit spots, so there's no SpotCard to gate against. Just Ask keeps
          the original suppress-while-awaiting-action behavior. */}
      {isFreeFormMode &&
        (isSessionCoaching || isHandAnalysis || !waitingForSpotAction) && (
        <InputBar
          draft={draft}
          setDraft={setDraft}
          disabled={isStreaming}
          onSubmit={() => sendMessage(draft)}
        />
      )}

      {openSheetId && (
        <SheetModal
          sheetId={openSheetId}
          onClose={() => setOpenSheetId(null)}
        />
      )}
    </div>
  );

  function sendActionChoice(action: SpotAction, spot: SpotPayload) {
    void sendMessage(formatActionChoice(action, spot));
  }
}

// ---------------------------------------------------------------------------
// Today's Drill choices — replaces the coach's "Next spot" choices in daily
// mode. Renders INLINE in the conversation flow (same place + style as the
// normal ChoicesBlock), advancing the ladder to the next rung or closing
// out the day.
// ---------------------------------------------------------------------------

function DailyDrillChoices({
  challenge,
  onWalkThrough,
}: {
  challenge: DailyChallenge;
  onWalkThrough: () => void;
}) {
  const next = nextIncompleteDifficulty(challenge);

  // Primary "continue" action gets the filled-accent (blue) treatment to
  // mark the forward path; the secondary follow-up uses the standard
  // bordered ChoicesBlock style.
  const primaryClass =
    "inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] rounded-lg bg-accent text-white text-sm font-medium hover:bg-accent/90 transition shadow-[0_1px_0_rgba(255,255,255,0.18)_inset,0_8px_20px_-8px_rgba(122,136,200,0.6)]";
  const secondaryClass =
    "inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] rounded-lg border border-border bg-surface text-sm font-medium text-text hover:bg-accent/10 hover:border-accent active:bg-accent/15 active:border-accent/80 transition";

  return (
    <div className="flex flex-wrap gap-2">
      {next ? (
        <Link
          to={dailyChallengeRoute({
            date: challenge.date,
            difficulty: next as DrillDifficulty,
            drillTarget: challenge.drillTarget,
            skillName: challenge.skillName,
          })}
          className={primaryClass}
        >
          Continue Today's Drill: {DIFFICULTY_LABEL[next]}
          <ChevronRight size={14} strokeWidth={ICON_STROKE} />
        </Link>
      ) : (
        <Link to="/drills" className={primaryClass}>
          Today's Drill complete: back to Drills
          <ChevronRight size={14} strokeWidth={ICON_STROKE} />
        </Link>
      )}
      <button type="button" onClick={onWalkThrough} className={secondaryClass}>
        Walk through the reasoning
      </button>
    </div>
  );
}

interface InputBarProps {
  draft: string;
  setDraft: (s: string) => void;
  disabled: boolean;
  onSubmit: () => void;
}

function InputBar({ draft, setDraft, disabled, onSubmit }: InputBarProps) {
  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  }

  return (
    <div className="border-t border-border bg-surface">
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-3 flex gap-2 items-end">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask…"
          rows={1}
          disabled={disabled}
          className="flex-1 resize-none bg-bg border border-border rounded-lg px-3 py-2 text-base md:text-sm text-text placeholder-text-muted focus:outline-none focus:border-accent disabled:opacity-50 min-h-[44px]"
        />
        <button
          type="button"
          disabled={disabled || !draft.trim()}
          onClick={onSubmit}
          className="px-4 min-h-[44px] rounded-lg bg-accent text-white font-medium text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-accent/90 transition"
        >
          Send
        </button>
      </div>
    </div>
  );
}
